import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import '../styles/LandingPage.css';

export default function LandingPage({ user }) {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [modalNotice, setModalNotice] = useState(null);

  // Dynamic greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 11) return 'Xayrli tong';
    if (hour >= 11 && hour < 18) return 'Xayrli kun';
    return 'Xayrli oqshom';
  };

  const [stats, setStats] = useState({
    users: 0,
    applications: 0,
    departments: 11,
    services: 12
  });

  const [weather, setWeather] = useState({
    temp: 20,
    condition: 'Musaffo osmon',
    icon: '☀️',
    city: 'Qarshi'
  });

  const [liveServices, setLiveServices] = useState({});

  useEffect(() => {
    // Live Weather based on browser geolocation with Qarshi fallback
    const fetchWeather = (lat, lon) => {
      let url = '/api/weather/live';
      if (lat !== undefined && lon !== undefined) {
        url += `?lat=${lat}&lon=${lon}`;
      }
      axios.get(url)
        .then((res) => {
          if (res.data && res.data.success) {
            setWeather({
              temp: res.data.temp,
              condition: res.data.condition || 'Musaffo osmon',
              icon: res.data.icon || '☀️',
              city: res.data.city || 'Qarshi'
            });
          }
        })
        .catch(() => {});
    };

    if (typeof window !== 'undefined' && 'geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          fetchWeather(latitude, longitude);
        },
        () => {
          // If geolocation permission is denied or unavailable, fallback to Qarshi
          fetchWeather();
        },
        { timeout: 8000, maximumAge: 300000 }
      );
    } else {
      fetchWeather();
    }

    // Dynamic real public stats from server database
    axios.get('/api/public/stats')
      .then((res) => {
        if (res.data) {
          setStats({
            users: res.data.users ?? 0,
            applications: res.data.applications ?? 0,
            departments: res.data.departments ?? 11,
            services: res.data.services ?? 12
          });
        }
      })
      .catch(() => {});

    // Dynamic real services metrics and deadlines
    axios.get('/api/services')
      .then((res) => {
        if (res.data && res.data.services) {
          const map = {};
          res.data.services.forEach((s) => {
            map[s.id] = s;
          });
          setLiveServices(map);
        }
      })
      .catch(() => {});
  }, []);

  // Sohalar (11 boʻlim / sektorlar) matching Image 1 & 5
  const departments = [
    {
      id: 'hemis',
      title: 'HEMIS - Oliy taʼlim jarayonlarini boshqarish axborot tizimi',
      color: '#10b981',
      bg: '#ecfdf5',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
          <path d="M6 12v5c3 3 9 3 12 0v-5" />
        </svg>
      )
    },
    {
      id: 'uslubiy',
      title: 'Universitet oʻquv-uslubiy kengashi',
      color: '#db2777',
      bg: '#fdf2f8',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#db2777" strokeWidth="2">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      )
    },
    {
      id: 'kengash',
      title: 'Universitet kengashi',
      color: '#ef4444',
      bg: '#fef2f2',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
          <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v4M12 14v4M16 14v4" />
        </svg>
      )
    },
    {
      id: 'xodimlar',
      title: 'Xodimlar boʻlimi',
      color: '#f97316',
      bg: '#fff7ed',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2">
          <circle cx="12" cy="7" r="4" />
          <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
        </svg>
      )
    },
    {
      id: 'rektorat',
      title: 'Rektorat - Rektor',
      color: '#059669',
      bg: '#f0fdf4',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      )
    },
    {
      id: 'yoshlar',
      title: 'Yoshlar bilan ishlash boʻlimi',
      color: '#0284c7',
      bg: '#f0f9ff',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      )
    },
    {
      id: 'ilmiy',
      title: 'Ilmiy boʻlim',
      color: '#16a34a',
      bg: '#f0fdf4',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
          <path d="M10 2v7.31M14 9.3V1.99M8.5 2h7M14 9.3a6.5 6.5 0 1 1-4 0" />
        </svg>
      )
    },
    {
      id: 'komplayens',
      title: 'Korrupsiyaga qarshi kurashish "Komplayens-nazorat" tizimini boshqarish boʻlimi',
      color: '#dc2626',
      bg: '#fef2f2',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      )
    },
    {
      id: 'qoshma',
      title: 'Qoʻshma taʼlim dasturlari',
      color: '#2563eb',
      bg: '#eff6ff',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      )
    },
    {
      id: 'xotinqizlar',
      title: 'Xotin-qizlar bilan ishlash va psixologik maslahat markazi',
      color: '#0891b2',
      bg: '#ecfeff',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0891b2" strokeWidth="2">
          <circle cx="12" cy="7" r="4" />
          <path d="M5.5 21v-2a6.5 6.5 0 0 1 13 0v2" />
        </svg>
      )
    },
    {
      id: 'sifat',
      title: 'Taʼlim sifatini nazorat qilish',
      color: '#0284c7',
      bg: '#f0f9ff',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )
    }
  ];

  // Eng yangi xizmatlar (matching Image 1 & 5)
    const getRatingBadge = (serviceId) => {
    const svc = liveServices[serviceId];
    if (svc && svc.reviewCount > 0 && svc.rating) {
      return Number(svc.rating).toFixed(1);
    }
    return '0.0';
  };

  const getCountBadge = (serviceId, fallback = 0) => {
    const svc = liveServices[serviceId];
    if (svc && svc.applicantCount !== undefined) {
      return String(svc.applicantCount);
    }
    return String(fallback);
  };

  // Eng yangi xizmatlar (matching Image 1 & 5)
  const newestServices = [
    {
      id: 'psixolog',
      title: 'Psixologga murojaat qilish',
      iconBg: '#ef4444',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      ),
      badges: [
        { icon: '⏱', label: String(liveServices['psixolog']?.timeToApply !== undefined ? liveServices['psixolog'].timeToApply : 4) },
        { icon: '👥', label: String(liveServices['psixolog']?.applicantCount !== undefined ? liveServices['psixolog'].applicantCount : 4) },
        { icon: '📅', label: liveServices['psixolog']?.processingDays || '7 kun' }
      ]
    },
    {
      id: 'korp-email',
      title: 'Professor-oʻqituvchilar, tadqiqotchilar va xodimlar uchun korporativ elektron pochta ochish uchun ariza',
      iconBg: '#0284c7',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      ),
      badges: [
        { icon: '⏱', label: String(liveServices['korp-email']?.timeToApply !== undefined ? liveServices['korp-email'].timeToApply : 8) },
        { icon: '★', label: getRatingBadge('korp-email') },
        { icon: '👥', label: getCountBadge('korp-email', stats.users || 0) },
        { icon: '📅', label: liveServices['korp-email']?.processingDays || '2 kun' }
      ]
    },
    {
      id: 'xotin-murojaat',
      title: 'Xotin-qizlar murojaatini koʻrib chiqish',
      iconBg: '#0284c7',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <circle cx="12" cy="7" r="4" />
          <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
        </svg>
      ),
      badges: [
        { icon: '⏱', label: String(liveServices['xotin-murojaat']?.timeToApply !== undefined ? liveServices['xotin-murojaat'].timeToApply : 4) },
        { icon: '★', label: getRatingBadge('xotin-murojaat') },
        { icon: '👥', label: getCountBadge('xotin-murojaat', 0) },
        { icon: '📅', label: liveServices['xotin-murojaat']?.processingDays || '7 kun' }
      ]
    },
    {
      id: 'pedagog-qabul',
      title: 'Pedagog xodimlarni tanlov asosida ishga qabul qilish',
      iconBg: '#f97316',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v8M8 12h8" />
        </svg>
      ),
      badges: [
        { icon: '⏱', label: String(liveServices['pedagog-qabul']?.timeToApply !== undefined ? liveServices['pedagog-qabul'].timeToApply : 5) },
        { icon: '★', label: getRatingBadge('pedagog-qabul') },
        { icon: '👥', label: getCountBadge('pedagog-qabul', 0) },
        { icon: '📅', label: liveServices['pedagog-qabul']?.processingDays || '30 kun' }
      ]
    }
  ];

  // Eng koʻp foydalaniladigan xizmatlar (matching Image 1 & 5)
  const popularServices = [
    {
      id: 'phd-exam',
      title: 'Chet tilidan va mutaxassislik fanlaridan malakaviy imtihon.',
      isPrimary: true, // This is the active requested service!
      iconBg: '#16a34a',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      ),
      badges: [
        { icon: '⏱', label: String(liveServices['phd-exam']?.timeToApply !== undefined ? liveServices['phd-exam'].timeToApply : 8) },
        { icon: '★', label: getRatingBadge('phd-exam') },
        { icon: '👥', label: getCountBadge('phd-exam', stats.applications || 0) },
        { icon: '📅', label: liveServices['phd-exam']?.processingDays || '30 kun' }
      ]
    },
    {
      id: 'hemis-grades',
      title: 'HEMIS - Talabalar reyting daftarchasi va fan baholarini koʻrish',
      iconBg: '#10b981',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      ),
      badges: [
        { icon: '⏱', label: '2 daqiqa' },
        { icon: '★', label: getRatingBadge('hemis-grades') },
        { icon: '👥', label: '1 250' },
        { icon: '📅', label: 'Onlayn' }
      ]
    },
    {
      id: 'qoshma-talim-qabul',
      title: 'Qoʻshma taʼlim qabuli uchun ariza',
      iconBg: '#3b82f6',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
      badges: [
        { icon: '⏱', label: String(liveServices['qoshma-talim-qabul']?.timeToApply !== undefined ? liveServices['qoshma-talim-qabul'].timeToApply : 10) },
        { icon: '★', label: getRatingBadge('qoshma-talim-qabul') },
        { icon: '👥', label: getCountBadge('qoshma-talim-qabul', 0) },
        { icon: '📅', label: liveServices['qoshma-talim-qabul']?.processingDays || '15 kun' }
      ]
    },
    {
      id: 'adabiyot-ekspertiza',
      title: 'Oʻquv adabiyotlarni ekspertizadan oʻtkazish',
      iconBg: '#db2777',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      ),
      badges: [
        { icon: '⏱', label: String(liveServices['adabiyot-ekspertiza']?.timeToApply !== undefined ? liveServices['adabiyot-ekspertiza'].timeToApply : 10) },
        { icon: '★', label: getRatingBadge('adabiyot-ekspertiza') },
        { icon: '👥', label: getCountBadge('adabiyot-ekspertiza', 0) },
        { icon: '📅', label: liveServices['adabiyot-ekspertiza']?.processingDays || '30 kun' }
      ]
    }
  ];

  const handleCategoryClick = (deptId) => {
    if (activeCategory === deptId) {
      setActiveCategory(null);
    } else {
      setActiveCategory(deptId);
      setTimeout(() => {
        const panel = document.getElementById('soha-services-panel');
        if (panel) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
  };

  // Har bir soha (boʻlim / sektor) ga mos xizmatlar roʻyxati
  const departmentServices = {
    hemis: [
      {
        id: 'hemis-grades',
        title: 'HEMIS - Talabalar reyting daftarchasi va fan baholarini koʻrish',
        iconBg: '#10b981',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '2 daqiqa' },
          { icon: '★', label: getRatingBadge('hemis-grades') },
          { icon: '👥', label: '1 250' },
          { icon: '📅', label: 'Onlayn' }
        ]
      },
      {
        id: 'hemis-schedule',
        title: 'HEMIS - Dars jadvallari va akademik qarzdorlik maʼlumotnomasi',
        iconBg: '#059669',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '3 daqiqa' },
          { icon: '★', label: getRatingBadge('hemis-schedule') },
          { icon: '👥', label: '890' },
          { icon: '📅', label: 'Onlayn' }
        ]
      },
      {
        id: 'hemis-password',
        title: 'HEMIS - Talaba shaxsiy kabineti parolini tiklash',
        iconBg: '#10b981',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '5 daqiqa' },
          { icon: '★', label: getRatingBadge('hemis-password') },
          { icon: '👥', label: '340' },
          { icon: '📅', label: '1 kun' }
        ]
      }
    ],
    uslubiy: [
      {
        id: 'adabiyot-ekspertiza',
        title: 'Oʻquv adabiyotlarni ekspertizadan oʻtkazish',
        iconBg: '#db2777',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: String(liveServices['adabiyot-ekspertiza']?.timeToApply || 10) },
          { icon: '★', label: getRatingBadge('adabiyot-ekspertiza') },
          { icon: '👥', label: getCountBadge('adabiyot-ekspertiza', 0) },
          { icon: '📅', label: liveServices['adabiyot-ekspertiza']?.processingDays || '30 kun' }
        ]
      },
      {
        id: 'sillabus-tasdiq',
        title: 'Oʻquv-uslubiy majmualar va fan sillabuslarini tasdiqlash',
        iconBg: '#ec4899',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '15 daqiqa' },
          { icon: '★', label: getRatingBadge('sillabus-tasdiq') },
          { icon: '👥', label: '45' },
          { icon: '📅', label: '10 kun' }
        ]
      },
      {
        id: 'ochiq-dars',
        title: 'Ochiq darslar jadvalini muvofiqlashtirish va monitoring',
        iconBg: '#db2777',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '5 daqiqa' },
          { icon: '★', label: getRatingBadge('ochiq-dars') },
          { icon: '👥', label: '28' },
          { icon: '📅', label: '3 kun' }
        ]
      }
    ],
    kengash: [
      {
        id: 'kengash-tavsiya',
        title: 'Ilmiy unvonlar (dotsent, professor) olish uchun Kengash tavsiyanomasi',
        iconBg: '#ef4444',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '15 daqiqa' },
          { icon: '★', label: getRatingBadge('kengash-tavsiya') },
          { icon: '👥', label: '18' },
          { icon: '📅', label: '15 kun' }
        ]
      },
      {
        id: 'kengash-qaror',
        title: 'Universitet Kengashi qarorlaridan koʻchirma olish',
        iconBg: '#dc2626',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '5 daqiqa' },
          { icon: '★', label: getRatingBadge('kengash-qaror') },
          { icon: '👥', label: '62' },
          { icon: '📅', label: '2 kun' }
        ]
      },
      {
        id: 'mavzu-tasdiq',
        title: 'Dissertatsiya mavzularini Kengashda tasdiqlash uchun ariza',
        iconBg: '#ef4444',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '10 daqiqa' },
          { icon: '★', label: getRatingBadge('mavzu-tasdiq') },
          { icon: '👥', label: '25' },
          { icon: '📅', label: '10 kun' }
        ]
      }
    ],
    xodimlar: [
      {
        id: 'pedagog-qabul',
        title: 'Pedagog xodimlarni tanlov asosida ishga qabul qilish',
        iconBg: '#f97316',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v8M8 12h8" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: String(liveServices['pedagog-qabul']?.timeToApply || 5) },
          { icon: '★', label: getRatingBadge('pedagog-qabul') },
          { icon: '👥', label: getCountBadge('pedagog-qabul', 0) },
          { icon: '📅', label: liveServices['pedagog-qabul']?.processingDays || '30 kun' }
        ]
      },
      {
        id: 'mehnat-tatil',
        title: 'Mehnat taʼtili va xizmat safari arizalarini topshirish',
        iconBg: '#ea580c',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '5 daqiqa' },
          { icon: '★', label: getRatingBadge('mehnat-tatil') },
          { icon: '👥', label: '78' },
          { icon: '📅', label: '3 kun' }
        ]
      },
      {
        id: 'ish-joy-malumot',
        title: 'Ish joyidan maʼlumotnoma va mehnat daftarchasidan koʻchirma olish',
        iconBg: '#f97316',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '3 daqiqa' },
          { icon: '★', label: getRatingBadge('ish-joy-malumot') },
          { icon: '👥', label: '140' },
          { icon: '📅', label: '1 kun' }
        ]
      }
    ],
    rektorat: [
      {
        id: 'rektor-qabul',
        title: 'Universitet Rektori shaxsiy qabuliga yozilish',
        iconBg: '#059669',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <circle cx="12" cy="7" r="4" />
            <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '5 daqiqa' },
          { icon: '★', label: getRatingBadge('rektor-qabul') },
          { icon: '👥', label: '84' },
          { icon: '📅', label: '5 kun' }
        ]
      },
      {
        id: 'rektor-murojaat',
        title: 'Rektor nomiga rasmiy murojaat va ariza yuborish',
        iconBg: '#047857',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '8 daqiqa' },
          { icon: '★', label: getRatingBadge('rektor-murojaat') },
          { icon: '👥', label: '112' },
          { icon: '📅', label: '7 kun' }
        ]
      }
    ],
    yoshlar: [
      {
        id: 'ttj-ariza',
        title: 'Talabalar turar joyi (TTJ - yotoqxona) uchun ariza topshirish',
        iconBg: '#0284c7',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '7 daqiqa' },
          { icon: '★', label: getRatingBadge('ttj-ariza') },
          { icon: '👥', label: '310' },
          { icon: '📅', label: '5 kun' }
        ]
      },
      {
        id: 'stipendiya-tanlov',
        title: 'Nomdor stipendiyalar va tanlovlarda ishtirok etish arizasi',
        iconBg: '#0369a1',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '10 daqiqa' },
          { icon: '★', label: getRatingBadge('stipendiya-tanlov') },
          { icon: '👥', label: '56' },
          { icon: '📅', label: '14 kun' }
        ]
      },
      {
        id: 'yoshlar-klub',
        title: 'Talabalar toʻgaraklari va yoshlar klublariga aʼzo boʻlish',
        iconBg: '#0284c7',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '3 daqiqa' },
          { icon: '★', label: getRatingBadge('yoshlar-klub') },
          { icon: '👥', label: '180' },
          { icon: '📅', label: 'Onlayn' }
        ]
      }
    ],
    ilmiy: [
      {
        id: 'phd-exam',
        title: 'Chet tilidan va mutaxassislik fanlaridan malakaviy imtihon.',
        isPrimary: true,
        iconBg: '#16a34a',
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: String(liveServices['phd-exam']?.timeToApply || 8) },
          { icon: '★', label: getRatingBadge('phd-exam') },
          { icon: '👥', label: getCountBadge('phd-exam', stats.applications || 0) },
          { icon: '📅', label: liveServices['phd-exam']?.processingDays || '30 kun' }
        ]
      },
      {
        id: 'doktorantura-qabul',
        title: 'Doktorantura (DSc, PhD) va stajyor-tadqiqotchilikka qabul arizasi',
        iconBg: '#15803d',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
            <path d="M6 12v5c3 3 9 3 12 0v-5" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '15 daqiqa' },
          { icon: '★', label: getRatingBadge('doktorantura-qabul') },
          { icon: '👥', label: getCountBadge('doktorantura-qabul', 0) },
          { icon: '📅', label: '20 kun' }
        ]
      },
      {
        id: 'maxsus-imtihon',
        title: 'Maxsus imtihon uchun ariza topshirish',
        iconBg: '#0284c7',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: String(liveServices['maxsus-imtihon']?.timeToApply || 8) },
          { icon: '★', label: getRatingBadge('maxsus-imtihon') },
          { icon: '👥', label: getCountBadge('maxsus-imtihon', 0) },
          { icon: '📅', label: liveServices['maxsus-imtihon']?.processingDays || '30 kun' }
        ]
      }
    ],
    komplayens: [
      {
        id: 'komplayens-anonim',
        title: 'Korrupsiyaviy holatlar haqida xabar berish (Anonim ishonch telefoni)',
        iconBg: '#dc2626',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '3 daqiqa' },
          { icon: '★', label: getRatingBadge('komplayens-anonim') },
          { icon: '👥', label: '14' },
          { icon: '📅', label: 'Darhol' }
        ]
      },
      {
        id: 'manfaatlar-deklaratsiya',
        title: 'Manfaatlar toʻqnashuvi toʻgʻrisida deklaratsiya topshirish',
        iconBg: '#b91c1c',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '8 daqiqa' },
          { icon: '★', label: getRatingBadge('manfaatlar-deklaratsiya') },
          { icon: '👥', label: '88' },
          { icon: '📅', label: '3 kun' }
        ]
      },
      {
        id: 'odob-axloq',
        title: 'Odob-axloq qoidalari buzilishi boʻyicha murojaat',
        iconBg: '#dc2626',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '5 daqiqa' },
          { icon: '★', label: getRatingBadge('odob-axloq') },
          { icon: '👥', label: '9' },
          { icon: '📅', label: '5 kun' }
        ]
      }
    ],
    qoshma: [
      {
        id: 'qoshma-talim-qabul',
        title: 'Qoʻshma taʼlim qabuli uchun ariza',
        iconBg: '#3b82f6',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: String(liveServices['qoshma-talim-qabul']?.timeToApply || 10) },
          { icon: '★', label: getRatingBadge('qoshma-talim-qabul') },
          { icon: '👥', label: getCountBadge('qoshma-talim-qabul', 0) },
          { icon: '📅', label: liveServices['qoshma-talim-qabul']?.processingDays || '15 kun' }
        ]
      },
      {
        id: 'erasmus-mevlana',
        title: 'Xalqaro talabalar almashinuv dasturlariga (Erasmus+, Mevlana) ariza',
        iconBg: '#2563eb',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '15 daqiqa' },
          { icon: '★', label: getRatingBadge('erasmus-mevlana') },
          { icon: '👥', label: getCountBadge('erasmus-mevlana', 0) },
          { icon: '📅', label: '20 kun' }
        ]
      },
      {
        id: 'xorijiy-maruza',
        title: 'Xorijiy professor-oʻqituvchilar maʼruzalariga roʻyxatdan oʻtish',
        iconBg: '#1d4ed8',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <circle cx="12" cy="7" r="4" />
            <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '4 daqiqa' },
          { icon: '★', label: getRatingBadge('xorijiy-maruza') },
          { icon: '👥', label: getCountBadge('xorijiy-maruza', 0) },
          { icon: '📅', label: 'Onlayn' }
        ]
      }
    ],
    xotinqizlar: [
      {
        id: 'psixolog',
        title: 'Psixologga murojaat qilish',
        iconBg: '#ef4444',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: String(liveServices['psixolog']?.timeToApply || 4) },
          { icon: '★', label: getRatingBadge('psixolog') },
          { icon: '👥', label: getCountBadge('psixolog', 0) },
          { icon: '📅', label: liveServices['psixolog']?.processingDays || '7 kun' }
        ]
      },
      {
        id: 'xotin-murojaat',
        title: 'Xotin-qizlar murojaatini koʻrib chiqish',
        iconBg: '#0891b2',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <circle cx="12" cy="7" r="4" />
            <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: String(liveServices['xotin-murojaat']?.timeToApply || 4) },
          { icon: '★', label: getRatingBadge('xotin-murojaat') },
          { icon: '👥', label: getCountBadge('xotin-murojaat', 0) },
          { icon: '📅', label: liveServices['xotin-murojaat']?.processingDays || '7 kun' }
        ]
      },
      {
        id: 'ijtimoiy-yordam',
        title: 'Ijtimoiy himoyaga muhtoj talaba qizlarga yordam fondi arizasi',
        iconBg: '#0e7490',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '8 daqiqa' },
          { icon: '★', label: getRatingBadge('ijtimoiy-yordam') },
          { icon: '👥', label: getCountBadge('ijtimoiy-yordam', 0) },
          { icon: '📅', label: '10 kun' }
        ]
      }
    ],
    sifat: [
      {
        id: 'sifat-sorovnoma',
        title: 'Taʼlim sifati va darslar samaradorligi boʻyicha anonim soʻrovnoma',
        iconBg: '#0284c7',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '5 daqiqa' },
          { icon: '★', label: getRatingBadge('sifat-sorovnoma') },
          { icon: '👥', label: '410' },
          { icon: '📅', label: 'Onlayn' }
        ]
      },
      {
        id: 'talaba-baho',
        title: 'Professor-oʻqituvchilar faoliyatini talabalar tomonidan baholash',
        iconBg: '#0369a1',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '6 daqiqa' },
          { icon: '★', label: getRatingBadge('talaba-baho') },
          { icon: '👥', label: '350' },
          { icon: '📅', label: 'Onlayn' }
        ]
      },
      {
        id: 'dastur-monitoring',
        title: 'Oʻquv rejalari va dasturlari monitoringi takliflarini yuborish',
        iconBg: '#0284c7',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        ),
        badges: [
          { icon: '⏱', label: '10 daqiqa' },
          { icon: '★', label: getRatingBadge('dastur-monitoring') },
          { icon: '👥', label: '19' },
          { icon: '📅', label: '7 kun' }
        ]
      }
    ]
  };

  const selectedDepartment = departments.find(d => d.id === activeCategory);
  const categoryServices = activeCategory ? (departmentServices[activeCategory] || []) : [];

  const handleServiceClick = (service) => {
    if (service.id === 'phd-exam') {
      // Navigates to Image 2 (Service detail page)
      navigate('/services/phd-exam');
    } else {
      const title = t(service.title);
      let message = 'Uşbu xizmat yaqin kunlarda tõliq işga tuşiriladi. Hozirda «Çet tilidan va mutaxassislik fanlaridan malakaviy imtihon» xizmati faol va tayyor.';
      if (lang === 'ru') {
        message = 'Данная услуга будет запущена в ближайшее время. В настоящее время активна услуга «Квалификационный экзамен по иностранному языку и специальным предметам».';
      } else if (lang === 'en') {
        message = 'This service will be launched soon. Currently, the "Qualifying examination in foreign languages and specialty subjects" service is active and ready.';
      }
      setModalNotice({ title, message });
    }
  };

  // Filtered lists based on search term
  const filteredDepartments = departments.filter((dept) =>
    dept.title.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const filteredNewestServices = newestServices.filter((srv) =>
    srv.title.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const filteredPopularServices = popularServices.filter((srv) =>
    srv.title.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const totalSearchResults =
    filteredDepartments.length + filteredNewestServices.length + filteredPopularServices.length;

  return (
    <div className="portal-home-page tu-portal-root">
      {/* Background ambient lighting orbs */}
      <div className="tu-ambient-glow orb-1" />
      <div className="tu-ambient-glow orb-2" />

      <div className="container tu-portal-container">
        {/* Administrator boʻlsa portal ustida qaytish xabari */}
        {user && user.role === 'admin' && (
          <div className="tu-admin-glass-banner">
            <div className="tu-admin-banner-left">
              <span className="tu-admin-badge">🛡️ {t('Administrator rejimi')}</span>
              <span className="tu-admin-banner-text">
                {t('Barcha arizalar monitoringi, komissiya natijalari va muddatlarni boshqarish paneli.')}
              </span>
            </div>
            <button className="tu-btn-to-admin" onClick={() => navigate('/admin')}>
              <span>{t('Admin boshqaruv paneliga oʻtish')}</span>
              <span className="tu-arrow">→</span>
            </button>
          </div>
        )}

        {/* Hero Section - Turon University Brand Identity */}
        <div className="tu-hero-card">
          <div className="tu-hero-content">
            <div className="tu-hero-meta-row">
              <span className="tu-institution-tag">
                <span className="tu-live-dot" />
                {t('Turon Universiteti • Rasmiy taʼlim portali')}
              </span>
              <div className="tu-hero-weather-pill" title={`${t(weather.city)}: ${t(weather.condition)} (${weather.temp}°C)`}>
                <span className="tu-weather-icon">{weather.icon}</span>
                <span className="tu-weather-data">{t(weather.city)} {weather.temp}°C</span>
                <span className="tu-weather-condition">{t(weather.condition)}</span>
              </div>
            </div>

            <div className="tu-hero-greet-row">
              <span className="tu-hero-greeting">{t(getGreeting())}, {user ? (user.name || t('Hurmatli foydalanuvchi')) : t('Hurmatli foydalanuvchi')}!</span>
              {user && (
                <span className={`tu-role-chip ${user.role === 'admin' ? 'admin' : ''}`}>
                  {user.role === 'admin' ? `🛡️ ${t('Administrator')}` : `🎓 ${t('Talabgor')}`}
                </span>
              )}
            </div>

            <h1 className="tu-hero-title">
              {t('Turon universiteti yagona interaktiv xizmatlar portali')}
            </h1>
            <p className="tu-hero-desc">
              {t('Universitetning barcha elektron taʼlim va ilmiy xizmatlari, onlayn arizalar qabuli hamda natijalarni monitoring qilishning yagona rasmiy platformasi.')}
            </p>

            {/* Quick Hero Action Buttons */}
            <div className="tu-hero-actions">
              {user ? (
                <>
                  <button
                    className="tu-btn-primary"
                    onClick={() => navigate('/services/phd-exam')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                    <span>{t('Imtihonga ariza topshirish')}</span>
                    <span className="tu-action-arrow">→</span>
                  </button>

                  <button
                    className="tu-btn-secondary"
                    onClick={() => navigate('/dashboard')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                    <span>{t('Mening arizalarim holati')}</span>
                  </button>

                  <button
                    className="tu-btn-ghost"
                    onClick={() => {
                      if (lang === 'ru') {
                        setModalNotice({
                          title: 'Инструкция по сдаче квалификационного экзамена',
                          message: '1. Войдите в систему или зарегистрируйтесь.\n2. Выберите услугу «Квалификационный экзамен».\n3. Выберите специальность и предмет, загрузите необходимые документы (диплом, письмо ректора, копию научной работы).\n4. После подтверждения администратором отобразятся время и адрес офлайн-экзамена.\n5. По окончании экзамена скачивается протокол комиссии и официальная ведомость с QR-кодом.'
                        });
                      } else if (lang === 'en') {
                        setModalNotice({
                          title: 'Qualifying Examination Guidelines',
                          message: '1. Log in or create an account.\n2. Select the "Qualifying Examination" service.\n3. Choose your specialty and subject, and upload the required documents (diploma, rector letter, research paper copy).\n4. Once confirmed by the admin, offline exam time and venue will appear.\n5. Upon completion, download the committee protocol and official QR-coded certificate.'
                        });
                      } else {
                        setModalNotice({
                          title: 'Malakaviy imtihon topşiriş yõriqnomasi',
                          message: '1. Tizimga kiring yoki rõyxatdan õting.\n2. Malakaviy imtihon xizmatini tanlang.\n3. Mutaxassislik va fanni tanlab, sõralgan hujjatlarni (diplom, rektor xati, ilmiy iş nusxasi) yuklang.\n4. Admin tasdiqlagaç offline imtihon vaqti va manzili çiqadi.\n5. Imtihon yakunida komissiya bayonnomasi va QR-kodli rasmiy qaydnoma yuklab olinadi.'
                        });
                      }
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <span>{t('Yoʻriqnoma')}</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="tu-btn-primary"
                    onClick={() => {
                      const el = document.getElementById('sohalar-section');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <rect x="3" y="3" width="7" height="7" />
                      <rect x="14" y="3" width="7" height="7" />
                      <rect x="14" y="14" width="7" height="7" />
                      <rect x="3" y="14" width="7" height="7" />
                    </svg>
                    <span>{t('Barcha xizmatlar katalogi')}</span>
                    <span className="tu-action-arrow">↓</span>
                  </button>

                  <button
                    className="tu-btn-ghost"
                    onClick={() => {
                      if (lang === 'ru') {
                        setModalNotice({
                          title: 'О портале интерактивных услуг',
                          message: 'Через единый портал интерактивных услуг Университета Турон студенты, исследователи, преподаватели и сотрудники могут пользоваться всеми электронными услугами университета онлайн, отправлять заявки и отслеживать результаты через личный кабинет.'
                        });
                      } else if (lang === 'en') {
                        setModalNotice({
                          title: 'About the Interactive Services Portal',
                          message: 'Through the Turon University unified interactive services portal, students, researchers, faculty, and staff can access all electronic university services online, submit applications, and monitor their status via their personal account.'
                        });
                      } else {
                        setModalNotice({
                          title: 'Interaktiv xizmatlar portali haqida',
                          message: 'Turon universiteti yagona interaktiv xizmatlar portali orqali talabalar, tadqiqotçilar, professor-õqituvçilar va xodimlar universitetning barça elektron xizmatlaridan onlayn foydalanişlari, arizalar yuborişlari hamda natijalarini şaxsiy kabinet orqali kuzatişlari mumkin.'
                        });
                      }
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <span>{t('Portal haqida')}</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Interactive Search Console */}
          <div className="tu-hero-search-box">
            <div className="tu-search-inner">
              <span className="tu-search-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                placeholder={t('Xizmat yoki boʻlim nomini qidiring (masalan: HEMIS, Imtihon, Rektorat, Psixolog)...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tu-search-input"
              />
              {searchTerm && (
                <button
                  className="tu-search-clear"
                  onClick={() => setSearchTerm('')}
                  title={t('Tozalash')}
                  type="button"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Quick Keyword Pills */}
            <div className="tu-search-tags">
              <span className="tu-search-tags-label">{t('Ommabop qidiruvlar:')}</span>
              <button type="button" className="tu-tag-btn" onClick={() => setSearchTerm('hemis')}>HEMIS</button>
              <button type="button" className="tu-tag-btn" onClick={() => setSearchTerm('imtihon')}>Imtihon</button>
              <button type="button" className="tu-tag-btn" onClick={() => setSearchTerm('kengash')}>{t('Ilmiy kengash')}</button>
              <button type="button" className="tu-tag-btn" onClick={() => setSearchTerm('rektor')}>{t('Rektorat')}</button>
              <button type="button" className="tu-tag-btn" onClick={() => setSearchTerm('xodim')}>{t('Xodimlar')}</button>
            </div>
          </div>
        </div>

        {/* Live Search status notification banner if searching */}
        {searchTerm.trim() && (
          <div className="tu-search-feedback-banner">
            <div className="tu-search-feedback-text">
              <span className="tu-feedback-badge">{t('Qidiruv natijasi')}</span>
              <span><strong>«{searchTerm}»</strong> {t('soʻrovi boʻyicha jami')} <strong>{totalSearchResults}</strong> {t('ta xizmat va boʻlim topildi.')}</span>
            </div>
            <button className="tu-btn-reset-search" onClick={() => setSearchTerm('')}>
              ✕ {t('Qidiruvni tozalash')}
            </button>
          </div>
        )}

        {/* 4 Interactive Statistics Bar */}
        <div className="tu-stats-grid">
          <div className="tu-stat-card">
            <div className="tu-stat-icon-box blue">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div className="tu-stat-data">
              <span className="tu-stat-number">{stats.users.toLocaleString()}</span>
              <span className="tu-stat-caption">{t('Faol foydalanuvchilar')}</span>
            </div>
          </div>

          <div className="tu-stat-card">
            <div className="tu-stat-icon-box emerald">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div className="tu-stat-data">
              <span className="tu-stat-number">{stats.applications.toLocaleString()}</span>
              <span className="tu-stat-caption">{t('Yuborilgan arizalar')}</span>
            </div>
          </div>

          <div className="tu-stat-card">
            <div className="tu-stat-icon-box purple">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <div className="tu-stat-data">
              <span className="tu-stat-number">{stats.departments}</span>
              <span className="tu-stat-caption">{t('Boʻlimlar va sektorlar')}</span>
            </div>
          </div>

          <div className="tu-stat-card">
            <div className="tu-stat-icon-box amber">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 14 14" />
              </svg>
            </div>
            <div className="tu-stat-data">
              <span className="tu-stat-number">{stats.services}</span>
              <span className="tu-stat-caption">{t('Interaktiv elektron xizmatlar')}</span>
            </div>
          </div>
        </div>

        {/* Bento Quick Actions Hub */}
        <div className="tu-bento-section">
          <div className="tu-bento-grid">
            {/* Bento Card 1: Elektron xizmatlar katalogi */}
            <div className="tu-bento-card" onClick={() => {
              const el = document.getElementById('sohalar-section');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}>
              <div className="tu-bento-top">
                <div className="tu-bento-icon-circle blue">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                </div>
                <span className="tu-bento-mini-label">{t('Katalog')}</span>
              </div>
              <h3 className="tu-bento-title">{t('Elektron xizmatlar')}</h3>
              <p className="tu-bento-desc">{t('Universitetning barcha taʼlim, ilmiy va maʼmuriy yoʻnalishdagi onlayn xizmatlari.')}</p>
              <div className="tu-bento-footer">
                <span className="tu-bento-link">{t('Xizmatlarni koʻrish')}</span>
                <span className="tu-circle-arrow">→</span>
              </div>
            </div>

            {/* Bento Card 2: Monitoring */}
            <div className="tu-bento-card" onClick={() => user ? navigate('/dashboard') : navigate('/login', { state: { infoMessage: t('Arizalarni koʻrish uchun tizimga kiring.') } })}>
              <div className="tu-bento-top">
                <div className="tu-bento-icon-circle blue">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>
                <span className="tu-bento-mini-label">{user ? t('Mening kabinetim') : t('Onlayn nazorat')}</span>
              </div>
              <h3 className="tu-bento-title">{user ? t('Mening arizalarim') : t('Arizalar holati')}</h3>
              <p className="tu-bento-desc">{t('Topshirilgan arizalarning joriy holati, komissiya xulosasi va rasmiy qaydnomani koʻrish.')}</p>
              <div className="tu-bento-footer">
                <span className="tu-bento-link">{user ? t('Arizalarimni koʻrish') : t('Tekshirish')}</span>
                <span className="tu-circle-arrow">→</span>
              </div>
            </div>

            {/* Bento Card 3: Soʻrovnomalar */}
            <div className="tu-bento-card" onClick={() => {
              if (lang === 'ru') {
                setModalNotice({
                  title: 'Опросы университета',
                  message: 'В ближайшие дни для пользователей системы будут опубликованы новые опросы по мониторингу качества образования и научных процессов.'
                });
              } else if (lang === 'en') {
                setModalNotice({
                  title: 'University Surveys',
                  message: 'New educational and academic quality monitoring surveys will be published for system users in the coming days.'
                });
              } else {
                setModalNotice({
                  title: 'Universitet sõrovnomalari',
                  message: 'Tizim foydalanuvçilari uçun yangi taʼlim va ilmiy sifat monitoringi sõrovnomalari yaqin kunlarda eʼlon qilinadi.'
                });
              }
            }}>
              <div className="tu-bento-top">
                <div className="tu-bento-icon-circle green">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </div>
                <span className="tu-bento-mini-label">{t('Sifat nazorati')}</span>
              </div>
              <h3 className="tu-bento-title">{t('Soʻrovnomalar')}</h3>
              <p className="tu-bento-desc">{t('Taʼlim sifati va ilmiy jarayonlar boʻyicha oʻz fikr-mulohazalaringizni bildiring.')}</p>
              <div className="tu-bento-footer">
                <span className="tu-bento-link">{t('Qatnashish')}</span>
                <span className="tu-circle-arrow">→</span>
              </div>
            </div>

            {/* Bento Card 4: Texnik yordam / Admin murojaat */}
            <div className="tu-bento-card" onClick={() => {
              if (lang === 'ru') {
                setModalNotice({
                  title: 'Центр помощи Университета Турон',
                  message: 'Контакты по обращениям и техническим вопросам:\n📞 Телефон: +998 (75) 220-00-25\n✉️ E-mail: turonuniversity@gmail.com\n🏛️ Адрес: г. Карши, ул. Насаф, 1/14'
                });
              } else if (lang === 'en') {
                setModalNotice({
                  title: 'Turon University Help Center',
                  message: 'Contact for inquiries and technical issues:\n📞 Phone: +998 (75) 220-00-25\n✉️ E-mail: turonuniversity@gmail.com\n🏛️ Address: Karshi city, Nasaf str., 1/14'
                });
              } else {
                setModalNotice({
                  title: 'Turon universiteti yordam markazi',
                  message: 'Murojaat va texnik masalalar bõyiça aloqa:\n📞 Telefon: +998 (75) 220-00-25\n✉️ E-mail: turonuniversity@gmail.com\n🏛️ Manzil: Qarşi şahri, Nasaf kõçasi, 1/14-uy'
                });
              }
            }}>
              <div className="tu-bento-top">
                <div className="tu-bento-icon-circle amber">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <span className="tu-bento-mini-label">{t('24/7 Qoʻllab-quvvatlash')}</span>
              </div>
              <h3 className="tu-bento-title">{t('Texnik yordam')}</h3>
              <p className="tu-bento-desc">{t('Tizim boʻyicha savollar, muammolar va maslahatlar uchun universitet aloqa markazi.')}</p>
              <div className="tu-bento-footer">
                <span className="tu-bento-link">{t('Bogʻlanish')}</span>
                <span className="tu-circle-arrow">→</span>
              </div>
            </div>
          </div>
        </div>

        {/* Global no search results state */}
        {searchTerm.trim() && totalSearchResults === 0 && (
          <div className="tu-empty-results-box">
            <div className="tu-empty-icon">🔎</div>
            <h3 className="tu-empty-title">
              «{searchTerm}» {t('soʻrovi boʻyicha hech qanday xizmat topilmadi')}
            </h3>
            <p className="tu-empty-text">
              {t('Qidiruv soʻzini toʻgʻri kiritganingizni tekshiring yoki quyidagi boʻlimlar orqali xizmatlarni koʻring.')}
            </p>
            <button className="tu-btn-primary" onClick={() => setSearchTerm('')}>
              {t('Barcha xizmatlarni koʻrsatish')}
            </button>
          </div>
        )}

        {/* Sohalar Section */}
        {filteredDepartments.length > 0 && (
          <div className="tu-section-block" id="sohalar-section">
            <div className="tu-section-heading">
              <div className="tu-section-heading-left">
                <span className="tu-section-pretitle">{t('Tuzilma va yoʻnalishlar')}</span>
                <h2 className="tu-section-main-title">{t('Universitet boʻlimlari va sohalari')}</h2>
              </div>
              <span className="tu-section-counter-pill">{t('Jami:')} {filteredDepartments.length} {t('ta soha')}</span>
            </div>

            <div className="tu-departments-grid">
              {filteredDepartments.map((dept) => {
                const count = departmentServices[dept.id]?.length || 0;
                const isSelected = activeCategory === dept.id;
                return (
                  <div
                    key={dept.id}
                    className={`tu-dept-card ${isSelected ? 'active' : ''}`}
                    onClick={() => handleCategoryClick(dept.id)}
                    title={`${t(dept.title)} (${count} ${t('ta xizmat')})`}
                  >
                    <div className="tu-dept-icon-wrapper" style={{ backgroundColor: dept.bg }}>
                      {dept.icon}
                    </div>
                    <div className="tu-dept-info">
                      <span className="tu-dept-name">{t(dept.title)}</span>
                      <div className="tu-dept-meta">
                        <span className="tu-dept-count-badge">{count} {t('ta xizmat')}</span>
                        <span className="tu-dept-action-text">{isSelected ? t('Yopish ✕') : t('Ochish →')}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tanlangan sohaga mos xizmatlar roʻyxati (Expanded Services Panel) */}
            {activeCategory && selectedDepartment && (
              <div className="tu-expanded-panel" id="soha-services-panel">
                <div className="tu-expanded-header">
                  <div className="tu-expanded-header-left">
                    <div
                      className="tu-expanded-badge"
                      style={{
                        backgroundColor: selectedDepartment.bg,
                        color: selectedDepartment.color,
                        borderColor: `${selectedDepartment.color}35`
                      }}
                    >
                      {selectedDepartment.icon}
                      <span>{t(selectedDepartment.title)}</span>
                    </div>
                    <span className="tu-expanded-count">
                      ⚡ <strong>{categoryServices.length}</strong> {t('ta mos elektron xizmat')}
                    </span>
                  </div>
                  <button
                    className="tu-btn-close-expanded"
                    onClick={() => setActiveCategory(null)}
                    type="button"
                  >
                    {t('✕ Yopish')}
                  </button>
                </div>

                <div className="tu-services-grid">
                  {categoryServices.map((service) => (
                    <div
                      key={service.id}
                      className="tu-service-card"
                    >
                      <div className="tu-card-header">
                        <div className="tu-card-icon-circle" style={{ backgroundColor: service.iconBg }}>
                          {service.icon}
                        </div>
                        <h3 className="tu-card-title">{t(service.title)}</h3>
                      </div>

                      <div className="tu-card-metrics">
                        {service.badges.map((b, idx) => (
                          <span key={idx} className="tu-metric-pill">
                            <span className="tu-metric-icon">{b.icon}</span>
                            <span className="tu-metric-val">{t(b.label)}</span>
                          </span>
                        ))}
                      </div>

                      <button
                        className="tu-btn-service-action"
                        onClick={() => handleServiceClick(service)}
                      >
                        <span>{t('Xizmatdan foydalanish')}</span>
                        <span className="tu-action-arrow">→</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Eng koʻp foydalaniladigan xizmatlar Section */}
        {filteredPopularServices.length > 0 && (
          <div className="tu-section-block">
            <div className="tu-section-heading">
              <div className="tu-section-heading-left">
                <span className="tu-section-pretitle">{t('Talab yuqori xizmatlar')}</span>
                <h2 className="tu-section-main-title">{t('Eng koʻp foydalaniladigan xizmatlar')}</h2>
              </div>
              <button
                className="tu-btn-see-all"
                onClick={() => {
                  if (lang === 'ru') {
                    setModalNotice({ title: 'Все услуги', message: 'Список всех услуг университета обновляется.' });
                  } else if (lang === 'en') {
                    setModalNotice({ title: 'All Services', message: 'The list of all university services is being updated.' });
                  } else {
                    setModalNotice({ title: 'Barça xizmatlar', message: 'Universitetning barça xizmatlar rõyxati yangilanmoqda.' });
                  }
                }}
              >
                {t('Barcha xizmatlar roʻyxati')} →
              </button>
            </div>

            <div className="tu-services-grid">
              {filteredPopularServices.map((service) => (
                <div
                  key={service.id}
                  className="tu-service-card"
                >
                  <div className="tu-card-header">
                    <div className="tu-card-icon-circle" style={{ backgroundColor: service.iconBg }}>
                      {service.icon}
                    </div>
                    <h3 className="tu-card-title">{t(service.title)}</h3>
                  </div>

                  <div className="tu-card-metrics">
                    {service.badges.map((b, idx) => (
                      <span key={idx} className="tu-metric-pill">
                        <span className="tu-metric-icon">{b.icon}</span>
                        <span className="tu-metric-val">{t(b.label)}</span>
                      </span>
                    ))}
                  </div>

                  <button
                    className="tu-btn-service-action"
                    onClick={() => handleServiceClick(service)}
                  >
                    <span>{t('Xizmatdan foydalanish')}</span>
                    <span className="tu-action-arrow">→</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Eng yangi xizmatlar Section */}
        {filteredNewestServices.length > 0 && (
          <div className="tu-section-block">
            <div className="tu-section-heading">
              <div className="tu-section-heading-left">
                <span className="tu-section-pretitle">{t('Yangi imkoniyatlar')}</span>
                <h2 className="tu-section-main-title">{t('Eng yangi raqamli xizmatlar')}</h2>
              </div>
              <button
                className="tu-btn-see-all"
                onClick={() => {
                  if (lang === 'ru') {
                    setModalNotice({ title: 'Все услуги', message: 'Список всех услуг университета обновляется.' });
                  } else if (lang === 'en') {
                    setModalNotice({ title: 'All Services', message: 'The list of all university services is being updated.' });
                  } else {
                    setModalNotice({ title: 'Barça xizmatlar', message: 'Universitetning barça xizmatlar rõyxati yangilanmoqda.' });
                  }
                }}
              >
                {t('Barcha yangiliklar')} →
              </button>
            </div>

            <div className="tu-services-grid">
              {filteredNewestServices.map((service) => (
                <div key={service.id} className="tu-service-card">
                  <div className="tu-card-header">
                    <div className="tu-card-icon-circle" style={{ backgroundColor: service.iconBg }}>
                      {service.icon}
                    </div>
                    <h3 className="tu-card-title">{t(service.title)}</h3>
                  </div>

                  <div className="tu-card-metrics">
                    {service.badges.map((b, idx) => (
                      <span key={idx} className="tu-metric-pill">
                        <span className="tu-metric-icon">{b.icon}</span>
                        <span className="tu-metric-val">{t(b.label)}</span>
                      </span>
                    ))}
                  </div>

                  <button
                    className="tu-btn-service-action"
                    onClick={() => handleServiceClick(service)}
                  >
                    <span>{t('Xizmatdan foydalanish')}</span>
                    <span className="tu-action-arrow">→</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Modal Notice */}
        {modalNotice && (
          <div className="tu-modal-overlay" onClick={() => setModalNotice(null)}>
            <div className="tu-modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="tu-modal-icon-badge">ℹ️</div>
              <h3 className="tu-modal-title">{modalNotice.title}</h3>
              <p className="tu-modal-text">{modalNotice.message}</p>
              <button className="tu-modal-btn-confirm" onClick={() => setModalNotice(null)}>
                {t('Tushunarli')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
